import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Pool, useCompanyGetPoolsQuery, usePoolDeletePoolMutation } from '../,./../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { Button } from '../../../lib/button'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { AddPoolModal } from '../../components/AddPoolModal'
import { RootState } from '../../redux/store'
import useText from '../../../common/hooks/useText'

export type PoolsPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const PoolsPage = memo(function PoolsPage({ className, style }: PoolsPageProps): React.ReactElement {
    const text = useText()
    const user = useSelector((state: RootState) => state.user)
    const [openAddModal, setOpenAddModal] = useState(false)

    const cancel = () => setOpenAddModal(false)

    const { currentData: pools, refetch } = useCompanyGetPoolsQuery(
        { companyId: user?.selectedCompanyId },
        { skip: !user.selectedCompanyId }
    )

    const [removePool] = usePoolDeletePoolMutation()

    const save = () => {
        notifySuccess(text('msgPoolAdded'))
        refetch()
        setOpenAddModal(false)
    }

    const removePools = async (poolId: string) => {
        if (confirm(text('msgRemovePool')))
            try {
                await removePool({ poolId, companyId: user.selectedCompanyId }).unwrap()
                notifySuccess(text('msgPoolRemoved'))
                refetch()
            } catch (e) {
                console.error(e)
                notifyError(text('msgPoolNotRemoved'))
            }
    }

    return (
        <PageLayoutContainer className={cn(className)} style={style}>
            <WidgetLayout
                title={<Text code="lblPoolsPage"></Text>}
                headerButtons={[
                    <Button code="btnAdd" data-testid="add-icon" small onClick={() => setOpenAddModal(true)}></Button>,
                ]}
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <Text code="lblName"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {pools?.map((pool: Pool) => (
                            <TableRow key={pool.id}>
                                <TableBodyCell>
                                    <Link
                                        data-testid="name-pools"
                                        to={`/pool/${pool.id}`}
                                        className="pointer table btn-link"
                                    >
                                        {pool.name}
                                    </Link>
                                </TableBodyCell>
                                <TableBodyCell>
                                    <div className="flex justify-content-end mr-3">
                                        <button
                                            data-testid="remove-pools"
                                            type="button"
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            title={text('btnRemove')}
                                            className="btn btn-danger btn-xs btn-icon"
                                            onClick={() => removePools(pool.id)}
                                        >
                                            <span className="fa fa-remove"></span>
                                        </button>
                                    </div>
                                </TableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {openAddModal && <AddPoolModal cancel={cancel} save={save} />}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
